import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { leadsService } from "../services/services";
import { CreateHouseholdParams, AddHouseholdMemberParams } from "../types/HouseholdMember";

// Query Keys
export const householdKeys = {
  all: ["households"] as const,
  lists: () => [...householdKeys.all, "list"] as const,
  list: (filters: string) => [...householdKeys.lists(), { filters }] as const,
  details: () => [...householdKeys.all, "detail"] as const,
  detail: (id: string) => [...householdKeys.details(), id] as const,
  leadHouseholds: (leadId: string) =>
    [...householdKeys.all, "lead", leadId] as const,
};

// Get household members for a lead
export const useGetLeadHouseholds = (leadId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: householdKeys.leadHouseholds(leadId),
    queryFn: () => leadsService.getLeadHouseholds(leadId),
    enabled: !!leadId && enabled,
    staleTime: 0, // Always fetch fresh data
    refetchOnMount: "always", // ALWAYS refetch when component mounts (even if data exists)
    refetchOnWindowFocus: true, // Refetch when window regains focus
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes to prevent flicker during updates
  });
};

// Create a new household with members
export const useCreateHousehold = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params: CreateHouseholdParams) =>
      leadsService.createHousehold(params),
    onSuccess: async (data, variables) => {
      // Only refetch (which also invalidates), don't invalidate separately to avoid flicker
      await queryClient.refetchQueries({
        queryKey: householdKeys.all,
      });
    },
  });
};

// Add a member to an existing household
export const useAddHouseholdMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      householdId,
      memberData,
    }: {
      householdId: string;
      memberData: AddHouseholdMemberParams;
    }) => leadsService.addHouseholdMember(householdId, memberData),
    onSuccess: async () => {
      // Only refetch to avoid flicker
      await queryClient.refetchQueries({
        queryKey: householdKeys.all,
      });
    },
  });
};

// Disband a household — all members become standalone leads
export const useDisbandHousehold = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId }: { householdId: string }) =>
      leadsService.disbandHousehold(householdId),
    onSuccess: async () => {
      // The whole household is gone, so every lead detail page that referenced it
      // is stale, not just this one. Refetch the household tree AND the lead lists.
      await queryClient.refetchQueries({ queryKey: householdKeys.all });
      await queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
};

// Put a picked lead in this lead's household
export const useLinkHouseholdLead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, memberLeadId }: { leadId: string; memberLeadId: string }) =>
      leadsService.linkHouseholdLead(leadId, memberLeadId),
    onSuccess: async () => {
      // Both leads' household sections change, and the lead list shows households.
      await queryClient.refetchQueries({ queryKey: householdKeys.all });
      await queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
};

// Restore a household member into the agent's own leads
export const useRestoreHouseholdMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId }: { leadId: string }) =>
      leadsService.restoreHouseholdMember(leadId),
    onSuccess: async () => {
      // The member moves into the agent's book, so lead lists change too.
      await queryClient.refetchQueries({ queryKey: householdKeys.all });
      await queryClient.invalidateQueries({ queryKey: ["leads"] });
    },
  });
};

// Remove a member from household
export const useRemoveHouseholdMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId }: { leadId: string }) =>
      leadsService.removeHouseholdMember(leadId),
    onSuccess: async () => {
      // Only refetch to avoid flicker
      await queryClient.refetchQueries({
        queryKey: householdKeys.all,
      });
    },
  });
};

// Update household member
export const useUpdateHouseholdMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ leadId, data }: { leadId: string; data: any }) =>
      leadsService.updateHouseholdMember(leadId, data),
    onSuccess: async () => {
      // Only refetch to avoid flicker
      await queryClient.refetchQueries({
        queryKey: householdKeys.all,
      });
    },
  });
};

// Switch primary household member
export const useSwitchPrimaryMember = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ householdId, newPrimaryLeadId }: { householdId: string; newPrimaryLeadId: string }) =>
      leadsService.switchPrimaryMember(householdId, newPrimaryLeadId),
    onSuccess: async () => {
      // Only refetch to avoid flicker
      await queryClient.refetchQueries({
        queryKey: householdKeys.all,
      });
    },
  });
};

// Get household details
export const useGetHousehold = (householdId: string, enabled: boolean = true) => {
  return useQuery({
    queryKey: householdKeys.detail(householdId),
    queryFn: () => leadsService.getHousehold(householdId),
    enabled: !!householdId && enabled,
  });
};